let __cacheSvc: any;
async function getCacheService() {
  if (!__cacheSvc) {
    const mod = await import('./cacheService');
    __cacheSvc = mod.cacheService;
    try { await __cacheSvc.init?.(); } catch {}
  }
  return __cacheSvc;
}

import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

// ✅ Storage seguro (fallback em memória quando localStorage não está disponível)
const memoryStorage: Storage = {
  getItem: (k: string) => (window as any).__mem?.[k] ?? null,
  setItem: (k: string, v: string) => {
    (window as any).__mem = (window as any).__mem || {};
    (window as any).__mem[k] = v;
  },
  removeItem: (k: string) => {
    if ((window as any).__mem) delete (window as any).__mem[k];
  },
  clear: () => { (window as any).__mem = {}; },
  key: (i: number) => Object.keys((window as any).__mem || {})[i] ?? null,
  get length() { return Object.keys((window as any).__mem || {}).length; }
};

function resolveStorage(): Storage {
  try {
    const k = '__sb_test__';
    window.localStorage.setItem(k, '1');
    window.localStorage.removeItem(k);
    return window.localStorage; // 👍 disponível
  } catch {
    console.warn('🔐 localStorage indisponível — usando storage em memória (sessão não persiste entre reloads)');
    return memoryStorage;
  }
}

// ✅ Cliente Supabase original (opções explícitas de auth)
export const supabaseOriginal = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    flowType: 'pkce',
    storage: resolveStorage(),
    // storageKey: 'acor-erp-auth', // <- opcional: use se quiser isolar chave do auth
  },
  // global: { headers: { 'x-acor-app': 'erp' } }, // opcional
})

// ✅ WRAPPER SIMPLES QUE DELEGA PARA O SUPABASE ORIGINAL + REALTIME
class SupabaseWithCache {
  private client = supabaseOriginal;
  // ✅ NOVO: Gerenciar subscriptions realtime
  private subscriptions = new Map<string, any>();

  // ✅ Método from que retorna o cliente original
  from(table: string) {
    return this.client.from(table);
  }

  // ✅ Método para cache manual de dados
  async cacheData(table: string, data: any[]) {
    try {
      const cs = await getCacheService();
      switch (table) {
        case 'obras':
          await cs.cacheObras(data);
          break;
        case 'tarefas':
          await cs.cacheTarefas(data);
          break;
        case 'tarefas_comentarios':
          await cs.cacheComentarios(data);
          break;
        case 'tarefas_responsaveis':
          await cs.cacheTarefasResponsaveis(data);
          break;
        case 'grupos_tarefas':
          await cs.cacheGruposTarefas(data);
          break;
        case 'checklist_instances':
          // Para checklist_instances, não cachear automaticamente por enquanto
          console.log(`📋 Cache: Checklist instances não cacheadas automaticamente`);
          break;
        case 'usuarios':
          // Para usuarios, não cachear automaticamente por enquanto
          console.log(`👥 Cache: Usuários não cacheados automaticamente`);
          break;
        case 'documentos':
          // Para documentos, não cachear automaticamente por enquanto
          console.log(`📄 Cache: Documentos não cacheados automaticamente`);
          break;
        case 'fotos':
          // Para fotos, não cachear automaticamente por enquanto
          console.log(`📸 Cache: Fotos não cacheadas automaticamente`);
          break;
        case 'videos':
          // Para videos, não cachear automaticamente por enquanto
          console.log(`🎥 Cache: Vídeos não cacheados automaticamente`);
          break;
        case 'checklist_photos':
          // Para checklist_photos, não cachear automaticamente por enquanto
          console.log(`📸 Cache: Fotos de checklist não cacheadas automaticamente`);
          break;
        // Adicionar mais tabelas conforme necessário
        default:
          console.log(`⚠️ Cache: Tabela ${table} não mapeada para cache automático`);
      }
    } catch (error) {
      console.warn(`⚠️ Cache: Erro ao cachear dados da tabela ${table} (ignorando):`, error);
    }
  }

  // ✅ Método para recuperar dados do cache
  async getCachedData(table: string, column?: string, value?: any): Promise<any[]> {
    try {
      const cs = await getCacheService();
      switch (table) {
        case 'obras':
          return await cs.getCachedObras();
        case 'tarefas':
          if (column === 'obra_id' && value) {
            return await cs.getCachedTarefasByObra(value);
          }
          if (column === 'status' && value) {
            return await cs.getCachedTarefasByStatus(value);
          }
          return [];
        case 'tarefas_comentarios':
          if (column === 'tarefa_id' && value) {
            return await cs.getCachedComentariosByTarefa(value);
          }
          return [];
        case 'tarefas_responsaveis':
          if (column === 'tarefa_id' && value) {
            return await cs.getCachedTarefasResponsaveisByTarefa(value);
          }
          return [];
        case 'grupos_tarefas':
          if (column === 'obra_id' && value) {
            return await cs.getCachedGruposTarefasByObra(value);
          }
          return [];
        default:
          console.log(`⚠️ Cache: Recuperação da tabela ${table} não implementada`);
          return [];
      }
    } catch (error) {
      console.error(`Erro ao recuperar cache da tabela ${table}:`, error);
      return [];
    }
  }

  // ✅ NOVO: Método para criar subscription realtime de notificações
  subscribeToNotifications(
    userId: string, 
    onNotification: (notification: any) => void,
    onError?: (error: any) => void
  ) {
    const subscriptionKey = `notifications_${userId}`;
    
    // Remover subscription anterior se existir
    if (this.subscriptions.has(subscriptionKey)) {
      this.subscriptions.get(subscriptionKey).unsubscribe();
    }

    console.log(`🔔 Criando subscription de notificações para usuário: ${userId}`);

    const channel = this.client
      .channel(`notifications:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notificacoes',
          filter: `usuario_id=eq.${userId}`
        },
        (payload) => {
          console.log('🔔 Nova notificação recebida via realtime:', payload.new);
          onNotification(payload.new);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notificacoes',
          filter: `usuario_id=eq.${userId}`
        },
        (payload) => {
          console.log('🔔 Notificação atualizada via realtime:', payload.new);
          onNotification(payload.new);
        }
      )
      .subscribe((status) => {
        console.log(`🔔 Status da subscription de notificações: ${status}`);
        if (status === 'CHANNEL_ERROR' && onError) {
          onError(new Error('Erro na subscription de notificações'));
        }
      });

    this.subscriptions.set(subscriptionKey, channel);
    return channel;
  }

  // ✅ NOVO: Método para criar subscription realtime de menções
  subscribeToMentions(
    userId: string,
    onMention: (mention: any) => void,
    onError?: (error: any) => void
  ) {
    const subscriptionKey = `mentions_${userId}`;
    
    // Remover subscription anterior se existir
    if (this.subscriptions.has(subscriptionKey)) {
      this.subscriptions.get(subscriptionKey).unsubscribe();
    }

    console.log(`💬 Criando subscription de menções para usuário: ${userId}`);

    const channel = this.client
      .channel(`mentions:${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notificacoes',
          filter: `usuario_id=eq.${userId} and tipo=eq.mencao`
        },
        (payload) => {
          console.log('💬 Nova menção recebida via realtime:', payload.new);
          onMention(payload.new);
        }
      )
      .subscribe((status) => {
        console.log(`💬 Status da subscription de menções: ${status}`);
        if (status === 'CHANNEL_ERROR' && onError) {
          onError(new Error('Erro na subscription de menções'));
        }
      });

    this.subscriptions.set(subscriptionKey, channel);
    return channel;
  }

  // ✅ NOVO: Método para criar subscription realtime genérica
  subscribeToTable(
    table: string,
    filter: string | null,
    event: 'INSERT' | 'UPDATE' | 'DELETE' | '*',
    callback: (payload: any) => void,
    subscriptionKey: string,
    onError?: (error: any) => void
  ) {
    // Remover subscription anterior se existir
    if (this.subscriptions.has(subscriptionKey)) {
      this.subscriptions.get(subscriptionKey).unsubscribe();
    }

    console.log(`📡 Criando subscription para tabela ${table} com chave ${subscriptionKey}`);

    let channelConfig: any = {
      event: event,
      schema: 'public',
      table: table
    };

    if (filter) {
      channelConfig.filter = filter;
    }

    const channel = this.client
      .channel(subscriptionKey)
      .on('postgres_changes', channelConfig, callback)
      .subscribe((status) => {
        console.log(`📡 Status da subscription ${subscriptionKey}: ${status}`);
        if (status === 'CHANNEL_ERROR' && onError) {
          onError(new Error(`Erro na subscription ${subscriptionKey}`));
        }
      });

    this.subscriptions.set(subscriptionKey, channel);
    return channel;
  }

  // ✅ NOVO: Método para remover subscription específica
  unsubscribe(subscriptionKey: string) {
    if (this.subscriptions.has(subscriptionKey)) {
      console.log(`🔌 Removendo subscription: ${subscriptionKey}`);
      this.subscriptions.get(subscriptionKey).unsubscribe();
      this.subscriptions.delete(subscriptionKey);
      return true;
    }
    return false;
  }

  // ✅ NOVO: Método para remover todas as subscriptions
  unsubscribeAll() {
    console.log(`🔌 Removendo todas as ${this.subscriptions.size} subscriptions`);
    this.subscriptions.forEach((channel, key) => {
      console.log(`🔌 Removendo subscription: ${key}`);
      channel.unsubscribe();
    });
    this.subscriptions.clear();
  }

  // ✅ NOVO: Método para listar subscriptions ativas
  getActiveSubscriptions(): string[] {
    return Array.from(this.subscriptions.keys());
  }

  // ✅ Propriedades originais do Supabase
  get auth() {
    return this.client.auth;
  }

  get storage() {
    return this.client.storage;
  }

  get realtime() {
    return this.client.realtime;
  }

  // ✅ Método functions para Edge Functions (NECESSÁRIO PARA CHATGPT)
  get functions() {
    return this.client.functions;
  }

  // ✅ Método channel para real-time subscriptions
  channel(name: string) {
    return this.client.channel(name);
  }

  // ✅ Método rpc para funções customizadas
  rpc(functionName: string, params?: any) {
    return this.client.rpc(functionName, params);
  }
}

// ✅ Export da instância com cache e realtime
export const supabase = new SupabaseWithCache();

// ✅ Garantir limpeza de subscriptions ao deslogar por qualquer motivo (expiração/refresh falhou)
supabaseOriginal.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_OUT') {
    try { supabase.unsubscribeAll(); } catch {}
  }
});

// ✅ Helper para verificar autenticação (mantido igual)
export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabaseOriginal.auth.getUser()
  if (error) {
    console.error('Error getting user:', error)
    return null
  }
  return user
}

// ✅ Helper para logout (mantido igual)
export const signOut = async () => {
  // ✅ NOVO: Remover todas as subscriptions ao fazer logout
  supabase.unsubscribeAll();
  
  const { error } = await supabaseOriginal.auth.signOut()
  if (error) {
    console.error('Error signing out:', error)
  }
  return error
}

// ✅ NOVO: Helper para inicializar subscriptions de um usuário
export const initializeUserSubscriptions = (
  userId: string,
  onNotification?: (notification: any) => void,
  onMention?: (mention: any) => void,
  onError?: (error: any) => void
) => {
  console.log(`🚀 Inicializando subscriptions para usuário: ${userId}`);
  
  const subscriptions = [];
  
  if (onNotification) {
    subscriptions.push(
      supabase.subscribeToNotifications(userId, onNotification, onError)
    );
  }
  
  if (onMention) {
    subscriptions.push(
      supabase.subscribeToMentions(userId, onMention, onError)
    );
  }
  
  return subscriptions;
}